// lib/list/factoryNavigation.ts
import type { InventoryEntityState, KernelHandle, UserContext } from '#types/types';
import { URW_ORGANISATION_ID } from '#lib/urwProjectDashboardAccess';
import { factoryImageId, factoryImageMention, factoryImageMentions } from './factoryImageMentions';

export const factoryLoadContext = (user: UserContext) => JSON.stringify([user.dbUserId, user.organisationId, user.authenticatedOrganisationId, user.isFaterAdmin, user.isOrganisationAdmin, user.isImpersonating, user.trueUser?.email]);
const loads = new WeakMap<KernelHandle, { context: string; promise: Promise<InventoryEntityState<'Organisation'> | null> }>();
export function loadFactoryChatBank(kernel: KernelHandle) {
  const context = factoryLoadContext(kernel.getState().userContext);
  const current = loads.get(kernel);
  if (current?.context === context) return current.promise;
  const previous = kernel.readEntityRuntimeState('Organisation', URW_ORGANISATION_ID).factoryLoad;
  kernel.patchEntityRuntimeState('Organisation', URW_ORGANISATION_ID, { factoryLoad: { context, status: previous?.context === context && previous.status === 'ready' ? 'ready' : 'loading' } });
  const promise = kernel.syncEntity('Organisation', URW_ORGANISATION_ID, { requireFresh: true }).then(result => {
    if (factoryLoadContext(kernel.getState().userContext) !== context) return null;
    if (result?.factory?.status !== 'ready') throw new Error('Factory could not be loaded. Try again.');
    kernel.patchEntityRuntimeState('Organisation', URW_ORGANISATION_ID, { factoryLoad: { context, status: 'ready' } });
    return result;
  }).catch(error => {
    if (factoryLoadContext(kernel.getState().userContext) === context) kernel.patchEntityRuntimeState('Organisation', URW_ORGANISATION_ID, { factoryLoad: { context, status: previous?.context === context && previous.status === 'ready' ? 'ready' : 'error', error: 'Factory could not be loaded. Try again.' } });
    throw error;
  }).finally(() => {
    if (loads.get(kernel)?.promise === promise) loads.delete(kernel);
  });
  loads.set(kernel, { context, promise });
  return promise;
}

/** Resolve live membership before navigating or inserting; never use a result's URL. */
export async function chooseFactoryReference(kernel: KernelHandle, sessionId: string, folderId: string, imageId?: string, action: 'open' | 'reference' = 'open', signal?: AbortSignal) {
  signal?.throwIfAborted();
  const state = kernel.getState();
  const userId = state.userContext.dbUserId;
  const projectId = kernel.getLastProjectId(sessionId);
  if (!userId || !projectId || state.activeChatSessionId !== sessionId || state.inventory.Project.registry[projectId]?.projectType !== 'LIST') throw new Error('Open this List conversation before choosing a Factory reference.');
  const organisation = await loadFactoryChatBank(kernel);
  signal?.throwIfAborted();
  const current = kernel.getState();
  if (current.userContext.dbUserId !== userId || current.activeChatSessionId !== sessionId || kernel.getLastProjectId(sessionId) !== projectId) return;
  const factory = organisation?.factory;
  const folder = factory?.status === 'ready' ? factory.referenceFolders.find(folder => folder.id === folderId) : undefined;
  const image = folder?.images.find(image => image.id === imageId);
  if (!folder || (imageId && !image)) throw new Error('This Factory reference is no longer available.');
  if (action === 'reference') {
    if (!image || current.isQueuingChatMessage) throw new Error('Wait for the current message before adding this image.');
    const draft = kernel.getChatPrompt();
    const identity = { folderId, imageId: image.id };
    if (!factoryImageMentions(draft).some(item => item.id === factoryImageId(identity))) {
      kernel.setChatPrompt(`${draft}${draft && !/\s$/.test(draft) ? ' ' : ''}${factoryImageMention(identity, image.name)} `);
    }
    kernel.setStudioSection('list');
    kernel.openLeftPanel();
    kernel.logUserAction('Factory image added to draft', { sessionId, folderId, imageId });
  } else {
    kernel.patchEntityRuntimeState('Organisation', URW_ORGANISATION_ID, { factoryNavigation: { folderId, imageId, sessionId, userId, requestId: crypto.randomUUID(), freshBank: true } });
    kernel.setStudioSection('factory');
    kernel.logUserAction('Factory reference opened', { sessionId, folderId, imageId });
  }
}
